/**
 * PATH: packages/job-hub/src/jobs/qa/qaDragCommit.ts
 * FILE_ID: JOBHUB-QA-DRAG-COMMIT-001
 * PURPOSE: Turn a grip drop into one existing reorder or move call.
 */
import { floorOrder, placeBefore, sameParent, subtreeIds } from './qaDragOrder'

export type QaDropKind = 'level' | 'location' | 'checklist' | 'row'

export interface QaDropRef {
  kind: QaDropKind
  id: string
}

export interface QaDropContext {
  floors: readonly { id: string }[]
  areas: readonly { id: string; floorId: string }[]
  checklists: readonly { id: string; areaId: string }[]
  items: readonly { id: string; parent_item?: string | null; checklistId: string }[]
}

export type QaDropCommand =
  | { type: 'reorder-floors'; order: { id: string; sort_order: number }[] }
  | { type: 'reorder-areas'; order: { id: string; sort_order: number }[] }
  | { type: 'move-areas'; nodeIds: string[]; parentId: string }
  | { type: 'reorder-checklists'; order: { id: string; sort_order: number }[] }
  | { type: 'move-checklists'; nodeIds: string[]; parentId: string }
  | { type: 'reorder-items'; ids: string[] }
  | { type: 'move-items'; nodeIds: string[]; parentId: string }

function orderOf(rows: readonly { id: string }[]): { id: string; sort_order: number }[] {
  return rows.map((row, sort_order) => ({ id: row.id, sort_order }))
}

export function commitQaDrop(
  from: QaDropRef,
  to: QaDropRef,
  ctx: QaDropContext,
): QaDropCommand | null {
  if (from.id === to.id && from.kind === to.kind) return null
  if (from.kind === 'level') {
    if (to.kind !== 'level') return null
    const order = floorOrder(ctx.floors, from.id, to.id)
    return order.length ? { type: 'reorder-floors', order } : null
  }
  if (from.kind === 'location') {
    const source = ctx.areas.find((area) => area.id === from.id)
    if (!source) return null
    const floorId = to.kind === 'level'
      ? to.id
      : to.kind === 'location'
        ? ctx.areas.find((area) => area.id === to.id)?.floorId
        : undefined
    if (!floorId) return null
    if (floorId === source.floorId && to.kind === 'location') {
      const siblings = ctx.areas.filter((area) => area.floorId === floorId)
      const placed = placeBefore(siblings, from.id, to.id)
      if (placed.every((row, index) => row.id === siblings[index]?.id)) return null
      return { type: 'reorder-areas', order: orderOf(placed) }
    }
    if (floorId !== source.floorId) return { type: 'move-areas', nodeIds: [from.id], parentId: floorId }
    return null
  }
  if (from.kind === 'checklist') {
    const source = ctx.checklists.find((row) => row.id === from.id)
    if (!source) return null
    if (to.kind === 'checklist') {
      const target = ctx.checklists.find((row) => row.id === to.id)
      if (!target) return null
      if (target.areaId !== source.areaId) return null
      const siblings = ctx.checklists.filter((row) => row.areaId === source.areaId)
      const placed = placeBefore(siblings, from.id, to.id)
      if (placed.every((row, index) => row.id === siblings[index]?.id)) return null
      return { type: 'reorder-checklists', order: orderOf(placed) }
    }
    if (to.kind === 'location' && to.id !== source.areaId) {
      return { type: 'move-checklists', nodeIds: [from.id], parentId: to.id }
    }
    return null
  }
  const source = ctx.items.find((row) => row.id === from.id)
  if (!source) return null
  if (to.kind === 'row') {
    const target = ctx.items.find((row) => row.id === to.id)
    if (!target) return null
    if (target.checklistId !== source.checklistId) {
      const ids = subtreeIds(ctx.items, from.id)
      return ids.length ? { type: 'move-items', nodeIds: ids, parentId: target.checklistId } : null
    }
    if (!sameParent(ctx.items, from.id, to.id)) return null
    const siblings = ctx.items.filter((row) => row.checklistId === source.checklistId)
    const placed = placeBefore(siblings, from.id, to.id)
    if (placed.every((row, index) => row.id === siblings[index]?.id)) return null
    return { type: 'reorder-items', ids: placed.map((row) => row.id) }
  }
  if (to.kind === 'checklist') {
    const ids = subtreeIds(ctx.items, from.id)
    if (!ids.length || source.checklistId === to.id) return null
    return { type: 'move-items', nodeIds: ids, parentId: to.id }
  }
  return null
}
